import { Timestamp } from 'firebase/firestore';

export enum TicketStatus {
  OPEN = 'open',
  IN_PROGRESS = 'in_progress',
  ANSWERED = 'answered',
  CLOSED = 'closed'
}

export enum TicketPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent'
}

export enum TicketType {
  ADMIN = 'admin',
  SOFTWARE = 'software',
  BILLING = 'billing',
  FEATURE_REQUEST = 'feature_request',
  OTHER = 'other'
}

export interface TicketResponse {
  id: string;
  ticketId: string;
  message: string;
  isStaffResponse: boolean;
  responderId: string;
  responderName: string;
  responderEmail: string;
  attachments?: string[];
  createdAt: Timestamp;
}

export interface SupportTicket {
  id: string;
  ticketNumber: string; // Format: TICK-YYYYMMDD-XXXX
  type: TicketType;
  subject: string;
  description: string;
  priority: TicketPriority;
  status: TicketStatus;
  
  // User information
  userId: string;
  userName: string;
  userEmail: string;
  userCompany?: string;
  userRole?: string;
  
  // Assignment
  assignedTo?: string;
  assignedToName?: string;
  assignedAt?: Timestamp;
  
  // Attachments
  attachments?: string[];
  
  // Timestamps
  createdAt: Timestamp;
  updatedAt: Timestamp;
  lastResponseAt?: Timestamp;
  closedAt?: Timestamp;
  autoCloseWarningAt?: Timestamp;

  // Archive fields
  isArchived?: boolean;
  archivedAt?: Timestamp;
  archivedBy?: string;

  // Response tracking
  responseCount: number;
  lastResponseIsStaff: boolean;
  
  // Metadata
  tags?: string[];
  category?: string;
  relatedTickets?: string[];
  /** Where the ticket came from ('web' default, 'mobile', 'promoted_note', …). */
  sourceOrigin?: string;
  /** Originating feedback/review-note doc id when promoted. */
  sourceRefId?: string;
  /** Auto-captured context (url/route/userAgent/viewport/appVersion). */
  context?: import('./supportHub.types').SubmissionContext;
  
  // Satisfaction
  satisfactionRating?: number;
  satisfactionComment?: string;
}

export interface TicketFilter {
  status?: TicketStatus[];
  priority?: TicketPriority[];
  type?: TicketType[];
  assignedTo?: string;
  userId?: string;
  dateFrom?: Date;
  dateTo?: Date;
  searchTerm?: string;
}

export interface TicketStats {
  total: number;
  open: number;
  inProgress: number;
  answered: number;
  closed: number;
  avgResponseTime: number; // in hours
  avgResolutionTime: number; // in hours
  satisfactionScore: number; // 1-5
}

export interface TicketSettings {
  id: string;
  autoCloseAfterDays: number;
  warningBeforeAutoCloseDays: number;
  emailNotifications: boolean;
  notificationEmails: string[];
  businessHours: {
    start: string; // "09:00"
    end: string; // "17:00"
    timezone: string;
    workDays: number[]; // 1-7 (Monday-Sunday)
  };
  responseTemplates: {
    id: string;
    name: string;
    message: string;
    category: string;
  }[];
  categories: string[];
  tags: string[];
  updatedAt: Timestamp;
  updatedBy: string;
}